import { LANGUAGE } from '@/env';

const formatDate = (date: Date) => date.toISOString().split('T')[0]; // YYYY-MM-DD

const today = new Date();
const startDateDefault = new Date(today);
startDateDefault.setDate(today.getDate() + 4);

const endDateDefault = new Date(today);
endDateDefault.setFullYear(today.getFullYear() + 2);

// Type definition for a single filter configuration
interface FilterConfig<T> {
  type:
    | StringConstructor
    | NumberConstructor
    | BooleanConstructor
    | ArrayConstructor;
  defaultValue: T;
}

// Type definition for the entire filter schema
export interface FilterSchema {
  adultCount: FilterConfig<number>;
  juniorCount: FilterConfig<number>;
  childCount: FilterConfig<number>;
  infantCount: FilterConfig<number>;
  cruiseDaysMax: FilterConfig<number | null>;
  sortBy: FilterConfig<string>;
  companyCode: FilterConfig<string | null>;
  cruiseOneWay: FilterConfig<string | null>;
  onlyPromo: FilterConfig<boolean>;
  flightMandatory: FilterConfig<boolean | null>;
  departurePorts: FilterConfig<string[]>;
  ships: FilterConfig<string[]>;
  destinations: FilterConfig<string[]>;
  fares: FilterConfig<string[]>;
  stopPorts: FilterConfig<string[]>;
  categories: FilterConfig<string[]>;
  stars: FilterConfig<string[]>;
  startDate: FilterConfig<string>;
  endDate: FilterConfig<string>;
  priceMin: FilterConfig<number>;
  priceMax: FilterConfig<number>;
  page: FilterConfig<number>;
}

/**
 * Defines the schema for all search filters.
 */
export const filterSchema: Readonly<FilterSchema> = {
  adultCount: { type: Number, defaultValue: 2 },
  juniorCount: { type: Number, defaultValue: 0 },
  childCount: { type: Number, defaultValue: 0 },
  infantCount: { type: Number, defaultValue: 0 },

  cruiseDaysMax: { type: Number, defaultValue: null },
  sortBy: { type: String, defaultValue: 'soon' },
  companyCode: { type: String, defaultValue: null },
  cruiseOneWay: { type: String, defaultValue: null },
  onlyPromo: { type: Boolean, defaultValue: false },
  flightMandatory: { type: Boolean, defaultValue: null },

  // Array of strings (will be handled as multiple query params, e.g., ?fares=F1&fares=F2)
  departurePorts: { type: String, defaultValue: [] },
  ships: { type: Array, defaultValue: [] },
  destinations: { type: Array, defaultValue: [] },
  fares: { type: Array, defaultValue: [] },
  stopPorts: { type: Array, defaultValue: [] },
  categories: { type: Array, defaultValue: [] },
  stars: { type: Array, defaultValue: [] },

  startDate: {
    type: String,
    defaultValue: formatDate(startDateDefault),
  },
  endDate: {
    type: String,
    defaultValue: formatDate(endDateDefault),
  },
  priceMin: { type: Number, defaultValue: 0 },
  priceMax: { type: Number, defaultValue: LANGUAGE === 'cs' ? 100000 : 4000 }, // cs: CZK, sk: EUR

  page: { type: Number, defaultValue: 1 },
};

// Type definition for the range schema
export interface RangeSchema {
  date: [keyof FilterSchema, keyof FilterSchema];
  price: [keyof FilterSchema, keyof FilterSchema];
}

export const rangeSchema: Readonly<RangeSchema> = {
  date: ['startDate', 'endDate'],
  price: ['priceMin', 'priceMax'],
};

export { startDateDefault, endDateDefault };
